import * as moment from 'moment';

import {
  CancellationFlowStatus,
  getCancellationFlowStatusLabel,
} from '../_enums/cancellation/cancellation-flow-status.enum';
import { getCentralisationInsurerLabel } from '../_enums/centralisation-insurer.enum';
import {
  ClaimStatus,
  getClaimStatusLabel,
} from '../_enums/claim/claim-status.enum';
import {
  claimTypeToInsuranceType,
  getClaimTypeLabel,
} from '../_enums/claim/claim-type.enum';
import {
  getInsuranceFlowLabel,
  getLeadStepFromFlowStatus,
  InsuranceFlowStatus,
} from '../_enums/insurance-flow/insurance-flow-status.enum';
import {
  getInsuranceTypeLabel,
  InsuranceType,
} from '../_enums/insurance-type.enum';
import {
  getLeadChangeReasonLabel,
  getPrimaryChangeReason,
} from '../_enums/lead-change-reason.enum';
import { getGlobalLeadStatus, LeadStatus } from '../_enums/lead-status.enum';
import { getLeadTypeLabel, LeadType } from '../_enums/lead-type.enum';
import {
  getPeriodicityLabel,
  getPolicyOriginLabel,
  getProposalStateLabel,
  PolicyOrigin,
  PolicyStatus,
  ProposalState,
} from '../_enums/proposal.enums';
import { getTaskTypeLabel, TaskType } from '../_enums/task-type.enum';
import {
  getInsuranceNameFromMetadata,
  toUTCWithoutChangingTime,
} from '../_helpers/utils';
import { CancellationFlowSummary } from './cancellation.types';
import { Claim } from './claim.types';
import { InsuranceFlowSummary } from './insurance-flow.types';
import { CancellationMetadata, Lead, PolicyUpdateMetadata } from './lead.types';
import { ProposalDetails } from './proposals.type';

export type DisplayElement = {
  id: string;
  displayId: string;
  /**
   * The affaire a row is grouped under when that is not its own `displayId`:
   * a cancellation shows its `RES-` id but belongs with the flows it ends or
   * replaces.
   */
  groupId?: string;
  partnerId?: string;
  agentId?: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  partnerName?: string;
  agentName?: string;
  type: string;
  state: string;
  leadType: LeadType;
  /** Used to handle some specific cases where a lead must be displayed as
   * a submitted contract due to the way the data is structured
   */
  displayedLeadType?: LeadType;
  status:
    | LeadStatus
    | ClaimStatus
    | ProposalState
    | PolicyStatus
    | InsuranceFlowStatus
    | CancellationFlowStatus;
  insuranceType?: InsuranceType;
  creationDate: string;
  lastUpdateDate?: string;
  userId: string;
  memberId?: string;
  memberName?: string;
  relation?: string;
  policyStartDate?: string;
  policyEndDate?: string;
  insuranceCompany?: string;
  subcategory?: string;
  customerSupportId?: string;
  customerSupportName?: string;
  leadId?: string;
  targetedInsuranceType?: InsuranceType;
  originalProposalId?: string;
  recommendedRenewalDate?: Date | null;
  // Fields used solely for the csv export
  address?: string;
  premiumAmount?: string;
  totalAmount?: string;
  periodicity?: string;
  coverages?: string;
  startDate?: string;
  endDate?: string;
  origin?: PolicyOrigin;
  policyId?: string;
  runningCancellationStatus?: CancellationFlowStatus | null;
};

/**
 * A flow's status badge, followed by an orange one when a cancellation of the
 * flow is under way, its step on hover.
 */
export const withRunningCancellationBadge = (
  statusBadge: string,
  status: CancellationFlowStatus | null | undefined
): string =>
  status
    ? `<div class="d-flex flex-wrap gap-1 py-1 lh-sm">${statusBadge}<span class="lty-badge warning" title="${getCancellationFlowStatusLabel(
        status
      )}">${$localize`Résiliation en cours`}</span></div>`
    : statusBadge;

/** The affaire group a row belongs to in the grouped flow lists. */
export const flowGroupKey = (element: DisplayElement): string =>
  element.groupId || element.displayId || element.id;

/**
 * A group's head stands for the affaire. `group` comes newest first, save the
 * head a list's filter may have chosen. An active policy heads the group over
 * that choice — the newest one, as the first found. Without one the list's
 * choice stands, except that a cancellation, however recent, never heads a
 * group holding a flow. Reorders `group` in place.
 */
export const keepFlowAtHead = (group: DisplayElement[]): DisplayElement[] => {
  let headIndex = group.findIndex(
    (element) => element.status === InsuranceFlowStatus.POLICY_ACTIVE
  );
  if (headIndex < 0 && group[0]?.leadType === LeadType.CANCELLATION) {
    headIndex = group.findIndex(
      (element) => element.leadType !== LeadType.CANCELLATION
    );
  }
  if (headIndex > 0) group.unshift(...group.splice(headIndex, 1));
  return group;
};

export const leadToDisplayElement = (lead: Lead): DisplayElement => ({
  leadType: LeadType.INSURANCE,
  displayedLeadType:
    lead.status === LeadStatus.ACCEPTED
      ? LeadType.OFFER_ACCEPTED
      : lead.status === LeadStatus.SENT ||
          lead.status === LeadStatus.WAITING_PAIEMENT
        ? LeadType.SUBMISSION
        : lead.status === LeadStatus.ACTIVE ||
            lead.status === LeadStatus.REJECTED
          ? LeadType.PROPOSAL
          : LeadType.INSURANCE,
  id: lead.leadId,
  displayId: lead.leadId,
  partnerId: lead.partnerId,
  agentId: lead.agentId,
  customerName:
    lead.memberDetails?.companyName ||
    lead.memberName ||
    (lead.familyMemberDetails
      ? `${lead.familyMemberDetails?.firstName} ${lead.familyMemberDetails?.lastName}`
      : `${lead.memberDetails?.firstName} ${lead.memberDetails?.lastName}`),
  customerEmail: lead.memberDetails?.email,
  partnerName: lead.partnerName,
  agentName: lead.agentName,
  state: getGlobalLeadStatus(lead.status, lead.insuranceType),
  type: `${getLeadTypeLabel(
    lead.status === LeadStatus.ACCEPTED ||
      lead.status === LeadStatus.WAITING_PAIEMENT
      ? LeadType.SUBMISSION
      : LeadType.INSURANCE,
    getPrimaryChangeReason(lead.changeReason)
  )} ${getInsuranceTypeLabel(lead.insuranceType)} ${(lead.metadata as PolicyUpdateMetadata)?.insuranceType ? getInsuranceTypeLabel((lead.metadata as PolicyUpdateMetadata)?.insuranceType) : ''}`,
  customerPhone: `${lead.memberDetails?.countryCode} ${lead.memberDetails?.mobile}`,
  status: lead.status,
  insuranceType: lead.insuranceType,
  creationDate: lead.createdOn,
  userId: lead.userId!,
  memberId: lead.familyMemberId,
  insuranceCompany: getInsuranceNameFromMetadata(
    lead.metadata,
    lead.insuranceType
  ),
  customerSupportId: lead.customerSupportId,
  customerSupportName: lead.customerSupportName,
  lastUpdateDate: lead.lastUpdatedOn,
  originalProposalId: lead.originalProposalId,
  targetedInsuranceType:
    (lead.metadata as CancellationMetadata)?.insuranceToCancel ??
    (lead.metadata as PolicyUpdateMetadata)?.insuranceType,
  address: `${lead.memberDetails.address}, ${lead.memberDetails.postCode}, ${lead.memberDetails.city}`,
  coverages:
    lead.metadata?.chosenBaseOffer?.productNames?.join(',') ??
    lead.metadata?.chosenComplementaryOffer?.productNames?.join(',') ??
    '',
  premiumAmount: lead.metadata?.chosenBaseOffer?.price?.toString()
    ? `${lead.metadata?.chosenBaseOffer?.price?.toString()} CHF`
    : lead.metadata?.chosenComplementaryOffer?.price?.toString()
      ? `${lead.metadata?.chosenComplementaryOffer?.price.toString()} CHF`
      : '',
  totalAmount: lead.metadata?.chosenBaseOffer?.price?.toString()
    ? `${lead.metadata?.chosenBaseOffer?.price?.toString()} CHF`
    : lead.metadata?.chosenComplementaryOffer?.price?.toString()
      ? `${lead.metadata?.chosenComplementaryOffer?.price.toString()} CHF`
      : '',
  startDate:
    lead.metadata?.startDate ?? lead.metadata?.insuranceStartDate ?? '',
  endDate: getEndDateFromLead(lead),
  subcategory:
    !!lead.metadata?.chosenBaseOffer ||
    !!lead.metadata?.chosenComplementaryOffer
      ? getInsuranceTypeLabel(lead.insuranceType)
      : '',
});

export const proposalToDisplayElement = (
  proposal: ProposalDetails
): DisplayElement => {
  const leadType =
    proposal.proposalState === ProposalState.POLICY_CREATED
      ? LeadType.POLICY
      : proposal.proposalState === ProposalState.OFFER_ACCEPTED
        ? LeadType.OFFER_ACCEPTED
        : proposal.proposalState === ProposalState.SIGNED_DOCS
          ? LeadType.SUBMISSION
          : LeadType.PROPOSAL;
  return {
    leadType,
    id: proposal.proposalId,
    displayId:
      leadType === LeadType.POLICY
        ? proposal.policyDetails.policyId
        : proposal.proposalId,
    partnerId: proposal.partnerId,
    agentId: proposal.agentDetails?.agentId,
    customerName:
      proposal.memberDetails?.companyName ||
      (proposal.familyMemberDetails
        ? `${proposal.familyMemberDetails?.firstName} ${proposal.familyMemberDetails?.lastName}`
        : `${proposal.memberDetails?.firstName} ${proposal.memberDetails?.lastName}`),
    customerEmail: proposal.memberDetails?.email,
    partnerName: proposal.partnerName,
    agentName: proposal.agentDetails?.agentName,
    state: getProposalStateLabel(proposal.proposalState),
    // TODO: handle i18n when implemented
    type: `${getLeadTypeLabel(leadType)} ${proposal?.policyDetails?.categoryDetails ? (proposal.policyDetails.categoryDetails.subCategoryNameInFrench ?? proposal.policyDetails.categoryDetails.subCategoryName) : getInsuranceTypeLabel(proposal.insuranceType)}`,
    customerPhone: `${proposal.memberDetails?.countryCode} ${proposal.memberDetails?.mobile}`,
    status:
      leadType === LeadType.POLICY
        ? proposal.policyDetails.policyStatus
        : proposal.proposalState,
    creationDate: proposal.createdOn,
    userId: proposal?.memberDetails?.memberId!,
    memberId: proposal.familyMemberDetails?.familyMemberId,
    insuranceCompany:
      proposal.policyDetails?.categoryDetails?.categoryNameInFrench ??
      proposal.policyDetails?.categoryDetails?.categoryName ??
      '',
    lastUpdateDate: proposal.lastUpdatedOn,
    customerSupportId: proposal.customerSupportId,
    customerSupportName: proposal.customerSupportName,
    insuranceType: proposal.insuranceType,
    leadId: proposal?.leadDetails?.leadId,
    originalProposalId: proposal?.originalProposalId,
    recommendedRenewalDate: proposal?.recommendedRenewalDate,
    address: `${proposal.memberDetails.address}, ${proposal.memberDetails.postCode}, ${proposal.memberDetails.city}`,
    premiumAmount: proposal.policyDetails?.premiumAmount
      ? `${proposal.policyDetails.premiumAmount} CHF`
      : '',
    totalAmount: proposal.policyDetails?.totalAmount
      ? `${proposal.policyDetails.totalAmount} CHF`
      : '',
    periodicity: proposal.policyDetails?.periodicity
      ? getPeriodicityLabel(proposal.policyDetails.periodicity)
      : '',
    coverages: proposal.policyDetails?.productDetails?.productList
      ? proposal.policyDetails.productDetails.productList
          .map(
            (x) => `${x.productNameInFrench ?? x.productName} : ${x.amount} CHF`
          )
          .join(';')
      : '',
    startDate: proposal.policyDetails?.startDate
      ? `${new Date(proposal.policyDetails.startDate).toLocaleDateString('fr-CH')}`
      : '',
    endDate: proposal.policyDetails?.endDate
      ? `${new Date(proposal.policyDetails.endDate).toLocaleDateString('fr-CH')}`
      : '',
    subcategory:
      proposal.policyDetails?.categoryDetails?.subCategoryNameInFrench ??
      proposal.policyDetails?.categoryDetails?.subCategoryName ??
      '',
    origin: proposal.leadDetails?.leadId
      ? PolicyOrigin.LEAD
      : proposal.centralisationId
        ? PolicyOrigin.CENTRALISATION
        : PolicyOrigin.IMPORT,
    policyId: proposal.policyDetails?.policyId,
  };
};

const getInsuranceCompanyFromFlow = (
  policyDetails: InsuranceFlowSummary['policyDetails'],
  metadata: InsuranceFlowSummary['leadMetadata']
): string => {
  const policyInsuranceCompany =
    policyDetails?.categoryDetails?.categoryNameInFrench ||
    policyDetails?.categoryDetails?.categoryName;

  if (policyInsuranceCompany || !metadata) {
    return policyInsuranceCompany ?? '';
  }

  const chosenOfferInsuranceCompany =
    metadata.chosenBaseOffer?.insuranceName ||
    metadata.chosenComplementaryOffer?.insuranceName;

  if (chosenOfferInsuranceCompany) {
    return chosenOfferInsuranceCompany;
  }

  switch (metadata.type) {
    // Add more cases if needed
    case InsuranceType.CENTRALISATION:
      return (
        metadata.insurersInfos
          ?.map((insurerInfo) =>
            insurerInfo?.companyId
              ? (insurerInfo.companyName ?? insurerInfo.companyId)
              : getCentralisationInsurerLabel(insurerInfo?.insurer ?? '')
          )
          .filter((insurer): insurer is string => !!insurer)
          .join(', ') ?? ''
      );
    default:
      return '';
  }
};

const getTargetedInsuranceTypeFromMetadata = (
  metadata: InsuranceFlowSummary['leadMetadata']
): InsuranceType | undefined => {
  if (!metadata) {
    return undefined;
  }

  switch (metadata.type) {
    case InsuranceType.CANCELLATION:
      return metadata.insuranceToCancel ?? undefined;
    case InsuranceType.POLICY_UPDATE:
      return metadata.insuranceType ?? undefined;
    case InsuranceType.CENTRALISATION: {
      const insuranceTypeFromInsurerInfo = metadata.insurersInfos
        ?.flatMap((insurerInfo) => insurerInfo?.insurances ?? [])
        .find((insuranceType) => insuranceType != null);

      if (insuranceTypeFromInsurerInfo != null) {
        return insuranceTypeFromInsurerInfo as InsuranceType;
      }

      return metadata.trackingInfo
        ?.flatMap((trackingInfo) => trackingInfo?.insurances ?? [])
        .map((insuranceInfo) => insuranceInfo?.insurance)
        .find(
          (insuranceType): insuranceType is InsuranceType =>
            insuranceType != null
        );
    }
    default:
      return undefined;
  }
};

export const insuranceFlowToDisplayElement = (
  flow: InsuranceFlowSummary
): DisplayElement => {
  const leadType = getLeadStepFromFlowStatus(flow.status) ?? LeadType.INSURANCE;
  const primaryChangeReason = getPrimaryChangeReason(flow.changeReason);

  return {
    leadType,
    displayedLeadType: leadType,
    id: flow.id,
    displayId: flow.displayId ?? flow.policyDetails?.policyId ?? flow.id,
    partnerId: flow.partnerId ?? undefined,
    agentId: flow.agentId ?? undefined,
    customerName: flow.customerName ?? '',
    partnerName: flow.partnerName ?? undefined,
    agentName: flow.agentName ?? undefined,
    state: getInsuranceFlowLabel(flow.status),
    type: `${getLeadTypeLabel(leadType)} ${primaryChangeReason ? getLeadChangeReasonLabel(primaryChangeReason) : ''} ${
      flow.insuranceType ? ` ${getInsuranceTypeLabel(flow.insuranceType)}` : ''
    }`,
    status: flow.status,
    insuranceType: flow.insuranceType ?? undefined,
    creationDate: flow.createdOn,
    lastUpdateDate: flow.lastUpdatedOn,
    userId: flow.userId ?? '',
    customerSupportId: flow.customerSupportId ?? undefined,
    customerSupportName: flow.customerSupportName ?? undefined,
    address: flow.address,
    premiumAmount: flow.policyDetails?.premiumAmount
      ? `${flow.policyDetails.premiumAmount} CHF`
      : '',
    totalAmount: flow.policyDetails?.totalAmount
      ? `${flow.policyDetails.totalAmount} CHF`
      : '',
    periodicity: flow.policyDetails?.periodicity
      ? getPeriodicityLabel(flow.policyDetails.periodicity)
      : '',
    coverages: flow.policyDetails?.productDetails?.productList
      ? flow.policyDetails.productDetails.productList
          .map(
            (x) => `${x.productNameInFrench ?? x.productName} : ${x.amount} CHF`
          )
          .join(';')
      : '',
    startDate: flow.policyDetails?.startDate
      ? `${new Date(flow.policyDetails.startDate).toLocaleDateString('fr-CH')}`
      : '',
    endDate: flow.policyDetails?.endDate
      ? `${new Date(flow.policyDetails.endDate).toLocaleDateString('fr-CH')}`
      : '',
    subcategory:
      flow.policyDetails?.categoryDetails?.subCategoryNameInFrench ??
      flow.policyDetails?.categoryDetails?.subCategoryName ??
      '',
    insuranceCompany: getInsuranceCompanyFromFlow(
      flow.policyDetails,
      flow.leadMetadata
    ),
    targetedInsuranceType: getTargetedInsuranceTypeFromMetadata(
      flow.leadMetadata
    ),
    recommendedRenewalDate: flow.recommendedRenewalDate,
    origin: flow.origin,
    policyId: flow.policyDetails?.policyId,
    runningCancellationStatus: flow.runningCancellationStatus,
  };
};

/**
 * A cancellation of the new aggregate as a row of the task and flow tables.
 * Its `RES-` id and the branch it ends are what tell it apart from one filed
 * the old way, below.
 */
export const cancellationFlowToDisplayElement = (
  cancellation: CancellationFlowSummary
): DisplayElement => ({
  leadType: LeadType.CANCELLATION,
  displayedLeadType: LeadType.CANCELLATION,
  id: cancellation.id,
  displayId: cancellation.displayId,
  // Only external policies' cancellations reach these lists: they group under
  // the platform affaire replacing the policy, if any.
  groupId: cancellation.replacementInsuranceFlow?.displayId,
  customerName: cancellation.userName ?? '',
  memberId: cancellation.memberId ?? undefined,
  memberName: cancellation.memberName ?? undefined,
  state: getCancellationFlowStatusLabel(cancellation.status),
  type: [
    getLeadTypeLabel(LeadType.CANCELLATION),
    getInsuranceTypeLabel(cancellation.insuranceType),
  ]
    .filter(Boolean)
    .join(' '),
  status: cancellation.status,
  insuranceType: cancellation.insuranceType,
  creationDate: cancellation.createdOn,
  lastUpdateDate: cancellation.lastUpdatedOn,
  userId: cancellation.userId ?? '',
  customerSupportId: cancellation.customerSupportId ?? undefined,
  customerSupportName: cancellation.customerSupportName ?? undefined,
  insuranceCompany: cancellation.insurerName ?? '',
});

export const claimToDisplayElement = (claim: Claim): DisplayElement => ({
  leadType: LeadType.CLAIM,
  id: claim.claimId,
  displayId: claim.claimData.claimNumber ?? claim.claimId,
  customerName: claim.userName,
  customerEmail: claim.userEmail,
  partnerName: claim.partnerName,
  agentName: claim.agentName,
  state: getClaimStatusLabel(claim.claimStatus),
  type: `${getLeadTypeLabel(LeadType.CLAIM)} ${getClaimTypeLabel(claim.claimType)}`,
  customerPhone: '',
  insuranceType: claimTypeToInsuranceType(claim.claimType),
  status: claim.claimStatus,
  creationDate: claim.createdOn,
  userId: claim.userId,
  lastUpdateDate: claim.lastUpdatedOn,
  customerSupportId: claim.customerSupportId,
  customerSupportName: claim.customerSupportName,
  address: claim.userAddress,
  insuranceCompany: claim.insurer,
});

export const getEndDateFromLead = (lead: Lead): string => {
  const { insuranceType, metadata } = lead;

  // Check if there's a chosen base offer
  if (!metadata?.chosenBaseOffer) {
    return '';
  }

  // Get the start date
  const startDate = metadata.startDate ?? metadata.insuranceStartDate;
  if (!startDate) {
    return '';
  }

  // Parse the start date with moment, specifying DD/MM/YYYY format
  const startMoment = moment(startDate, 'DD/MM/YYYY');

  switch (insuranceType) {
    case InsuranceType.TRAVEL:
    case InsuranceType.MOTOR_VEHICLE:
      // Return startDate + 1 year
      return startMoment.add(1, 'year').format('DD/MM/YYYY');

    case InsuranceType.LEGAL_PROTECTION:
      // Get the insurance name
      const insuranceName = getInsuranceNameFromMetadata(
        metadata,
        insuranceType
      );

      if (insuranceName === 'Emilia') {
        //  Emilia insurance subscription is valid for 1 year
        return startMoment.add(1, 'year').format('DD/MM/YYYY');
      }
      // Otherwise, it's 3 years
      return startMoment.add(3, 'years').format('DD/MM/YYYY');

    default:
      return '';
  }
};

export const displayElementToCsvRow = (element: DisplayElement): string => {
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    return toUTCWithoutChangingTime(dateString)
      .tz('Europe/Zurich')
      .format('DD.MM.yyyy HH:mm');
  };

  const escapeCsvField = (field: string | undefined | null): string => {
    if (field === null || field === undefined) return '""';
    const str = String(field);
    // Escape quotes and wrap all fields in quotes
    return `"${str.replace(/"/g, '""')}"`;
  };

  const getTypeLabel = (): string => {
    if (element.insuranceType) {
      return getInsuranceTypeLabel(element.insuranceType);
    }
    return '';
  };

  const getAffaireLabel = (): string =>
    getLeadTypeLabel(element.displayedLeadType ?? element.leadType);

  const fields = [
    escapeCsvField(element.displayId),
    escapeCsvField(element.customerName),
    escapeCsvField(element.customerEmail),
    escapeCsvField(element.customerPhone),
    escapeCsvField(element.address),
    escapeCsvField(element.partnerName),
    escapeCsvField(element.agentName),
    escapeCsvField(getTypeLabel()),
    escapeCsvField(getAffaireLabel()),
    escapeCsvField(formatDate(element.creationDate)),
    escapeCsvField(element.state),
    escapeCsvField(
      element.lastUpdateDate ? formatDate(element.lastUpdateDate) : ''
    ),
    escapeCsvField(element.customerSupportName),
    escapeCsvField(element.insuranceCompany),
    escapeCsvField(element.subcategory),
    escapeCsvField(element.coverages),
    escapeCsvField(element.startDate ? element.startDate : ''),
    escapeCsvField(element.endDate ? element.endDate : ''),
    escapeCsvField(element.premiumAmount),
    escapeCsvField(element.totalAmount),
    escapeCsvField(element.periodicity),
    escapeCsvField(getPolicyOriginLabel(element.origin)),
  ];

  return fields.join(',');
};

export const TASKS_CSV_HEADERS = [
  $localize`ID`,
  $localize`Client`,
  $localize`Email client`,
  $localize`N° téléphone client`,
  $localize`Adresse`,
  $localize`Partenaire`,
  $localize`Conseiller`,
  $localize`Type`,
  $localize`Affaire`,
  $localize`Date de création`,
  $localize`Statut`,
  $localize`Màj`,
  $localize`Chargé de clientèle`,
  $localize`Compagnie d\'assurance`,
  $localize`Produit`,
  $localize`Couvertures`,
  $localize`Date de début`,
  $localize`Date de fin`,
  $localize`Prime`,
  $localize`Prime annuelle`,
  $localize`Périodicité`,
  $localize`Provenance`,
];
